import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {getPublicFile} from 'helpers/envHelper'
import styled from 'styled-components'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import useHealthCenterBrainFunctionMappingStyles from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Style'
import TableFiguresByPart from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Elements/tableFiguresByPart'
import useLangEn from 'hooks/useLangEn'

import {useAppSelector} from 'hooks'
import {
  selectLanguageReport,
  getCurrentLanguageReport,
} from 'features/languageReport/languageReportSlice'
import useLanguage from 'features/languageReport/useLanguage'

export const PageWrap = styled.div`
  background: White;
`

export const PageContent = styled.div`
  width: 1160px;
`

export const CardContent = styled.div`
  font-size: 14px;
`

export const FlexBox = styled.div`
  display: flex;
  padding: 0 15px;
  justify-content: space-between;
`
const getHealthReportReportImage = getPublicFile('images/health_center_report')

interface BrainFunctionMappingProps {
  Frontal_LH: number
  Temporal_LH: number
  agingRoiScore: Record<string, any>
}

const getNumber = (value: number | string) => {
  if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
  return 0
}

const scoreToClass = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'LoweringActive'
  }
  if (Math.floor(score) > 70) {
    return 'ExaltationActive'
  }

  return 'Balanced'
}

const scoreSign = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (parsedScore < 30) {
    return getHealthReportReportImage('down.svg')
  }
  if (parsedScore > 70) {
    return getHealthReportReportImage('up.svg')
  }

  return getHealthReportReportImage('arrow.svg')
}

const Label = ({score}: {score: number | string}) => {
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const {i18n} = useTranslation()

  const currentLanguageReport = getCurrentLanguageReport(
    languageReport,
    i18n.language,
  )

  const getText = () => {
    if (Math.floor(Number(score)) < 30) {
      return currentLanguageReport?.health?.brain_mapping.status.low
    }
    if (Math.floor(Number(score)) > 70) {
      return currentLanguageReport?.health?.brain_mapping.status.high
    }

    return currentLanguageReport?.health?.brain_mapping.status.normal
  }

  const className = scoreToClass(getNumber(score))
  return (
    <div className={`${classes.labelScore} ${className}`}>
      {getText()}
      <img src={scoreSign(score)} alt='icon' />
    </div>
  )
}

const Score = ({score}: {score: number}) => {
  const {t} = useTranslation()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  return (
    <div className={classes.bottom16}>
      <div className={classes.scoreNumber}>
        {score}
        {t('IPoints')}
      </div>
      <Label score={score} />
    </div>
  )
}

function TablePrintF1({
  Frontal_LH,
  Temporal_LH,
  agingRoiScore,
}: BrainFunctionMappingProps) {
  const {t} = useTranslation()
  const isEn = useLangEn()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const {i18n} = useTranslation()

  const currentLanguageReport = getCurrentLanguageReport(
    languageReport,
    i18n.language,
  )
  const getHeadTable = useCallback(
    (number: number) => {
      let firstColumnText =
        currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
          .score_table.headers[0] || ''
      if (number === 2) {
        firstColumnText =
          currentLanguageReport?.health?.brain_mapping.advanced.section.temporal
            .score_table.headers[0] || ''
      }
      if (number === 3) {
        firstColumnText =
          currentLanguageReport?.health?.brain_mapping.advanced.section.parietal
            .score_table.headers[0] || ''
      }
      if (number === 4) {
        firstColumnText =
          currentLanguageReport?.health?.brain_mapping.advanced.section
            .occipital.score_table.headers[0] || ''
      }
      return [
        firstColumnText,
        currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
          .score_table.headers[1] || '',
        currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
          .score_table.headers[2] || '',
        currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
          .score_table.headers[3] || '',
        currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
          .score_table.headers[4] || '',
      ]
    },
    [getCurrentLanguageReport],
  )

  const bodyTable = useMemo(
    () => [
      {
        data: [
          {
            header: [
              {
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[0].rois[0].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[0].rois[0].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[0].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF1.png'),
          },
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[0].functions,
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[0].symptoms,
          [getNumber(agingRoiScore?.Frontal_Pole_LH)],
          [getNumber(agingRoiScore?.Frontal_Pole_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[0].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[1].rois[0].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[0].translation,
              },
              {
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[1].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[1].rois[1].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[1].translation,
              },
              {
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[2].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[1].rois[2].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[1].rois[2].translation,
              },
            ],
            image: getHealthReportReportImage('brainF2.png'),
          },
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[1].functions,
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[1].symptoms,
          [
            getNumber(agingRoiScore?.Pars_Opercularis_LH),
            getNumber(agingRoiScore?.Pars_Orbitalis_LH),
            getNumber(agingRoiScore?.Pars_Triangularis_LH),
          ],
          [
            getNumber(agingRoiScore?.Pars_Opercularis_RH),
            getNumber(agingRoiScore?.Pars_Orbitalis_RH),
            getNumber(agingRoiScore?.Pars_Triangularis_RH),
          ],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F5.',
                // content: 'Rostral Middle Frontal',
                // note: t('IRostralNoteF5'),
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[2].rois[0].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[2].rois[0].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[2].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF5.png'),
          },
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[2].functions,
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[2].symptoms,
          [getNumber(agingRoiScore?.Rostral_Middle_Frontal_LH)],
          [getNumber(agingRoiScore?.Rostral_Middle_Frontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F6.',
                // content: 'Caudal Middle Frontal',
                // note: t('ICaudalMiddle'),
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[3].rois[0].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[3].rois[0].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[3].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF6.png'),
          },
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[3].functions,
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[3].symptoms,
          [getNumber(agingRoiScore?.Caudal_Middle_Frontal_LH)],
          [getNumber(agingRoiScore?.Caudal_Middle_Frontal_RH)],
        ],
        background: 'White',
      },
      {
        data: [
          {
            header: [
              {
                // key: 'F7.',
                // content: 'Superior Frontal',
                // note: t('IFrontalNoteF7'),
                key: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[4].rois[0].code,
                content:
                  currentLanguageReport?.health?.brain_mapping.advanced.section
                    .frontal.score_table.rows[4].rois[0].name,
                note: currentLanguageReport?.health?.brain_mapping.advanced
                  .section.frontal.score_table.rows[4].rois[0].translation,
              },
            ],
            image: getHealthReportReportImage('brainF7.png'),
          },
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[4].functions,
          currentLanguageReport?.health?.brain_mapping.advanced.section.frontal
            .score_table.rows[4].symptoms,
          [getNumber(agingRoiScore?.Superior_Frontal_LH)],
          [getNumber(agingRoiScore?.Superior_Frontal_RH)],
        ],
        background: 'White',
      },
    ],
    [isEn, currentLanguageReport],
  )

  console.log(currentLanguageReport)

  return (
    <div className={classes.brainFunctionMapping}>
      <PageWrap>
        <PageContent>
          <WrapElement
            number={1}
            text={
              currentLanguageReport?.health?.brain_mapping.advanced.section
                .frontal.title || ''
            }
          >
            <CardContent>
              <FlexBox>
                <div className={classes.contentDetailedAnalysis}>
                  <div className={classes.textHeader}>
                    <span>
                      {
                        currentLanguageReport?.health?.brain_mapping.advanced
                          .section.frontal.description[0]
                      }
                    </span>
                    &nbsp;
                    <span className={classes.headerColorGreen}>
                      {/* {t('IResOfHightOrder')} */}
                      {
                        currentLanguageReport?.health?.brain_mapping.advanced
                          .section.frontal.description[1]
                      }
                    </span>
                    &nbsp;
                    <span>{t('IMainArea')}</span>
                  </div>
                  <div className={classes.textContent}>
                    {/* <p>{t('IGovernsHigher')}</p>
                               <p>{t('IRepresentativeExample')}</p> */}
                    <p>
                      {
                        currentLanguageReport?.health?.brain_mapping.advanced
                          .section.frontal.description[2]
                      }
                    </p>
                    <p>
                      {
                        currentLanguageReport?.health?.brain_mapping.advanced
                          .section.frontal.description[3]
                      }
                    </p>
                    <p>
                      {
                        currentLanguageReport?.health?.brain_mapping.advanced
                          .section.frontal.description[4]
                      }
                    </p>
                    {/* <p>{t('IDetailedAnalysis')}</p>
                               <p>{t('IHealthLevelOfEach')}</p>
                               <p>{t('ISuggestedLowerNumber')}</p> */}
                  </div>
                </div>
                <div className={classes.imgDetailedAnalysis}>
                  <div className={classes.scoreSpace}>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            currentLanguageReport?.health?.brain_mapping
                              .advanced.section.frontal.score_by_side.left
                          }
                        </div>
                        <Score score={getNumber(Frontal_LH)} />
                      </div>
                    </div>
                    <div className={classes.scoreContainer}>
                      <div>
                        <div className={classes.headerScore}>
                          {
                            currentLanguageReport?.health?.brain_mapping
                              .advanced.section.frontal.score_by_side.right
                          }
                        </div>
                        <Score score={getNumber(Temporal_LH)} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.imageOverviewBrain}>
                    <img src={getHealthReportReportImage('brain.svg')} alt='' />
                  </div>
                </div>
              </FlexBox>
            </CardContent>
          </WrapElement>
          <div className={classes.marginBottom60}>
            <TableFiguresByPart
              head={getHeadTable(1)}
              body={bodyTable as any}
              isLocalLanguage={false}
            />
          </div>
        </PageContent>
      </PageWrap>
    </div>
  )
}

export default TablePrintF1
